import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/**
 * Connection tests prove a just-connected integration actually works (its credentials are accepted and it can
 * do its job) before the merchant sees it as connected. See docs/integration-connection-test.md.
 */

/** Why a connection test failed. Testers pick the closest match; the UI words its guidance from it. */
export enum ConnectionTestFailureReason {
  CREDENTIALS = "credentials",
  PERMISSIONS = "permissions",
  CONFIGURATION = "configuration",
  ADDRESS = "address",
  NO_RATES = "no_rates",
  UNREACHABLE = "unreachable",
  UNKNOWN = "unknown",
}

/**
 * Whether a failure proves the integration is misconfigured. Inconclusive failures (no rates, unreachable,
 * unknown) can come from the test itself, so they never mark an integration Not verified.
 */
const CONCLUSIVE_FAILURE: Record<ConnectionTestFailureReason, boolean> = {
  [ConnectionTestFailureReason.CREDENTIALS]: true,
  [ConnectionTestFailureReason.PERMISSIONS]: true,
  [ConnectionTestFailureReason.CONFIGURATION]: true,
  [ConnectionTestFailureReason.ADDRESS]: true,
  [ConnectionTestFailureReason.NO_RATES]: false,
  [ConnectionTestFailureReason.UNREACHABLE]: false,
  [ConnectionTestFailureReason.UNKNOWN]: false,
};

function isConnectionTestFailureReason(
  reason: string,
): reason is ConnectionTestFailureReason {
  return reason in CONCLUSIVE_FAILURE;
}

/** Takes the stored string so any family's reason can be checked; an unrecognized reason is inconclusive. */
export function isConclusiveFailureReason(
  reason: string | null | undefined,
): boolean {
  return (
    !!reason &&
    isConnectionTestFailureReason(reason) &&
    CONCLUSIVE_FAILURE[reason]
  );
}

const connectionTestOutcomeSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("ok"),
    /** Optional proof shown to the merchant, e.g. "Fetched 4 rates". */
    detail: z.string().nullish(),
  }),
  z.object({
    status: z.literal("failed"),
    reason: createEnum(ConnectionTestFailureReason, {
      enumName: "ConnectionTestFailureReason",
    }),
    message: z.string(),
    /** The input the merchant should fix, when the tester can tell. */
    field: z.string().nullish(),
  }),
]);
export const ConnectionTestOutcomeSchema: z.ZodType<ConnectionTestOutcome> =
  connectionTestOutcomeSchema;
export type ConnectionTestOutcome = z.infer<typeof connectionTestOutcomeSchema>;

export function connectionOk(detail?: string): ConnectionTestOutcome {
  return { status: "ok", detail: detail ?? null };
}

export function connectionFailed(
  reason: ConnectionTestFailureReason,
  message: string,
  field?: string,
): ConnectionTestOutcome {
  return { status: "failed", reason, message, field: field ?? null };
}

/** Which storage generation an integration lives in; each has one adapter on the server. */
export enum ConnectionTestFamily {
  /** Mongo `integrations` collection, keyed by Gen II discriminator kind. */
  GEN2 = "gen2",
  /** CRDB `integration` table, keyed by IntegrationKind. */
  GEN3 = "gen3",
  /** Merchant carrier accounts, keyed by RateProvider. */
  CARRIER = "carrier",
}

/** One concrete integration to test. */
export const ConnectionTestTargetSchema = z.object({
  family: createEnum(ConnectionTestFamily, {
    enumName: "ConnectionTestFamily",
  }),
  kind: z.string(),
  integrationId: z.string(),
});
export interface ConnectionTestTarget extends z.infer<
  typeof ConnectionTestTargetSchema
> {}

/** The integration_kind stored with a result, e.g. "gen2:KlaviyoIntegration" or "carrier:fedex". */
export function connectionTestKindKey(
  family: ConnectionTestFamily,
  kind: string,
): string {
  return `${family}:${kind}`;
}

/**
 * Query param asking a page to test an integration on load. A connect flow that leaves the page (a full-page OAuth
 * redirect) sets it on its return URL, since the integration is already listed when the page comes back.
 */
export const TEST_CONNECTION_PARAM = "testConnection";

function formatConnectionTestParam(target: ConnectionTestTarget): string {
  return `${target.family}:${target.kind}:${target.integrationId}`;
}

export function parseConnectionTestParam(
  value: string | null,
): ConnectionTestTarget | null {
  if (!value) {
    return null;
  }
  const [family, kind, integrationId, ...rest] = value.split(":");
  const parsed = ConnectionTestTargetSchema.safeParse({
    family,
    kind,
    integrationId,
  });
  return parsed.success && rest.length === 0 ? parsed.data : null;
}

/** Adds TEST_CONNECTION_PARAM to a return URL, keeping any query it already has. */
export function withConnectionTestParam(
  url: string,
  target: ConnectionTestTarget,
): string {
  const separator = url.includes("?") ? "&" : "?";
  const param = new URLSearchParams({
    [TEST_CONNECTION_PARAM]: formatConnectionTestParam(target),
  });
  return `${url}${separator}${param}`;
}
